import { liveQuery } from "dexie";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import type { StockRuntimeItem } from "../runtime/gateway/local-projection-client";
import type { StockTransferRuntimeProjection } from "../runtime/gateway/stock-transfer-client";
import { commitStockOperationalAction } from "../runtime/local-store/stock-action-state";
import { readStockActionRuntime } from "../runtime/local-store/stock-action-runtime";
import {
  readStockTransferLocalItems,
  readStockTransferRuntime,
} from "../runtime/local-store/stock-transfer-runtime";
import type { TrustedTerminalIdentity } from "../runtime/local-store/terminal-state";
import { StockTransferWorkspace } from "./StockTransferWorkspace";
import "./StockActionPanel.css";

type Props = {
  identity: TrustedTerminalIdentity;
  deviceSecret: string;
  sessionToken: string;
  item: StockRuntimeItem;
  online: boolean;
  onInternalUse: () => void;
};

type TransferWorkspaceState = {
  items: StockRuntimeItem[];
  runtime: StockTransferRuntimeProjection;
};

function requestKey(prefix: string) {
  const id = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${id}`;
}

function errorMessage(value: string) {
  if (value.includes("STOCK_ACTION_ONLINE_REQUIRED") || value.includes("GATEWAY_UNREACHABLE")) {
    return "รายการนี้ยังต้องเชื่อมต่ออินเทอร์เน็ตเพื่อยืนยันกับ Stock Core";
  }
  if (value.includes("STOCK_ACTION_PERMISSION_REQUIRED")) return "คุณไม่มีสิทธิ์ทำรายการนี้ในสาขาปัจจุบัน";
  if (value.includes("STOCK_ACTION_NOTE_REQUIRED")) return "กรุณาระบุรายละเอียดก่อนบันทึก";
  if (value.includes("INSUFFICIENT_STOCK_FOR_ITEM") || value.includes("NEGATIVE_STOCK_NOT_ALLOWED")) return "จำนวนที่ทำรายการมากกว่ายอดที่ Stock Core อนุญาต";
  if (value.includes("INITIAL_STOCK_COUNT_REQUIRED")) return "รายการนี้ยังไม่มี Canonical Stock State สำหรับทำรายการ";
  if (value.includes("STOCK_TRACKING_POLICY_REQUIRED") || value.includes("STOCK_TRACKING_UNIT_REQUIRED")) return "รายการนี้ยังตั้งค่าหน่วยสต็อกไม่สมบูรณ์";
  if (value.includes("POS_EMPLOYEE_RUNTIME_SESSION_INVALID")) return "เซสชันพนักงานหมดอายุ กรุณาเข้าสู่ระบบพนักงานใหม่";
  return "บันทึกรายการสต็อกไม่สำเร็จ กรุณาลองใหม่";
}

export function StockActionPanel({ identity, deviceSecret, sessionToken, item, online, onInternalUse }: Props) {
  const [runtime, setRuntime] = useState<Awaited<ReturnType<typeof readStockActionRuntime>>>(null);
  const [transferRuntime, setTransferRuntime] = useState<StockTransferRuntimeProjection | null>(null);
  const [transferWorkspace, setTransferWorkspace] = useState<TransferWorkspaceState | null>(null);
  const [wasteOpen, setWasteOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState("");
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [activeRequestKey, setActiveRequestKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const subscription = liveQuery(() => readStockActionRuntime(identity)).subscribe({
      next: setRuntime,
      error: () => setRuntime(null),
    });
    return () => subscription.unsubscribe();
  }, [identity.branch_id, identity.node_id, identity.organization_id]);

  useEffect(() => {
    const subscription = liveQuery(() => readStockTransferRuntime(identity)).subscribe({
      next: setTransferRuntime,
      error: () => setTransferRuntime(null),
    });
    return () => subscription.unsubscribe();
  }, [identity.branch_id, identity.node_id, identity.organization_id]);

  useEffect(() => {
    setWasteOpen(false);
    setTransferWorkspace(null);
    setReasonCode("");
    setQuantity("");
    setNote("");
    setActiveRequestKey("");
    setError(null);
    setSuccess(null);
  }, [item.stock_item_id]);

  const internalUseOptions = useMemo(
    () => runtime?.items.filter((option) => option.available && option.effect_class === "OPERATIONAL_USE") ?? [],
    [runtime],
  );
  const wasteOptions = useMemo(
    () => runtime?.items.filter((option) => option.available && (option.effect_class === "WASTE" || option.effect_class === "LOSS")) ?? [],
    [runtime],
  );
  const selectedReason = wasteOptions.find((option) => option.reason_code === reasonCode) ?? null;
  const currentQuantity = item.quantity_on_hand == null ? null : Number(item.quantity_on_hand);
  const hasStock = currentQuantity != null && Number.isFinite(currentQuantity) && currentQuantity > 0;
  const transferAvailable = Boolean(
    transferRuntime?.can_send
    && transferRuntime.eligible_destinations.some((destination) => destination.eligible_stock_item_ids.includes(item.stock_item_id)),
  );

  function openWaste() {
    if (!wasteOptions.length) return;
    setWasteOpen(true);
    setReasonCode("");
    setQuantity("");
    setNote("");
    setActiveRequestKey(requestKey("pos-stock-action"));
    setError(null);
    setSuccess(null);
  }

  function closeWaste() {
    if (busy) return;
    setWasteOpen(false);
    setError(null);
  }

  async function openTransfer() {
    if (!transferRuntime || !transferAvailable) return;
    const items = await readStockTransferLocalItems(identity);
    items.sort((left, right) => left.canonical_name.localeCompare(right.canonical_name, "th"));
    setTransferWorkspace({ items, runtime: transferRuntime });
    setError(null);
    setSuccess(null);
  }

  async function submitWaste() {
    if (!selectedReason || !activeRequestKey) return;
    const parsed = Number(quantity);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError("กรุณากรอกจำนวนที่มากกว่า 0");
      return;
    }
    if (currentQuantity != null && parsed > currentQuantity) {
      setError("จำนวนที่ทำรายการมากกว่ายอดคงเหลือในเครื่อง");
      return;
    }
    if (selectedReason.requires_note && !note.trim()) {
      setError("กรุณาระบุรายละเอียดก่อนบันทึก");
      return;
    }
    if (selectedReason.execution_mode !== "ONLINE_REQUIRED" || !online) {
      setError("รายการนี้ยังต้องเชื่อมต่ออินเทอร์เน็ตเพื่อยืนยันกับ Stock Core");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const result = await commitStockOperationalAction({
        identity,
        deviceSecret,
        sessionToken,
        stockItemId: item.stock_item_id,
        quantityDelta: -parsed,
        reasonCode: selectedReason.reason_code,
        note: note.trim() || null,
        requestKey: activeRequestKey,
      });
      setSuccess(
        result.readbackOk
          ? `${selectedReason.label} ${parsed.toLocaleString("th-TH")} รายการเรียบร้อยแล้ว`
          : `${selectedReason.label} บันทึกแล้ว · กำลังซิงค์ยอดล่าสุดจาก Stock Core`,
      );
      setWasteOpen(false);
      setActiveRequestKey("");
    } catch (cause) {
      setError(errorMessage(cause instanceof Error ? cause.message : "POS_STOCK_ACTION_COMMIT_FAILED"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="pos-stock-actions" aria-labelledby="pos-stock-actions-title">
        <div className="pos-stock-actions__heading">
          <div>
            <h3 id="pos-stock-actions-title">ดำเนินการ</h3>
            <p>ใช้ Action ตามกฎและสิทธิ์จาก MORE Stock</p>
          </div>
          {!online ? <span className="pos-stock-actions__offline">ออฟไลน์</span> : null}
        </div>

        {success ? <div className="pos-stock-actions__success">{success}</div> : null}

        <div className="pos-stock-actions__grid">
          {internalUseOptions.length ? (
            <button type="button" onClick={onInternalUse}>
              <strong>เบิกใช้ในร้าน</strong>
              <span>เพิ่มหลายสินค้าและบันทึกพร้อมกันใน 1 Batch</span>
            </button>
          ) : null}

          {wasteOptions.length ? (
            <button type="button" disabled={!hasStock} onClick={openWaste}>
              <strong>ของเสีย / ทิ้ง</strong>
              <span>Quick Action สำหรับเหตุการณ์ที่เกิดเป็นครั้งคราว</span>
            </button>
          ) : null}

          {transferAvailable ? (
            <button type="button" onClick={() => void openTransfer()}>
              <strong>โอนสาขา</strong>
              <span>เลือกปลายทางและส่งหลายสินค้าใน 1 Transfer</span>
            </button>
          ) : null}
        </div>

        {!runtime ? (
          <p className="pos-stock-actions__hint">กำลังรอ Action Policy จาก Local Projection ครั้งแรก</p>
        ) : (
          <p className="pos-stock-actions__hint">การอ่านและจัดชุดใช้ข้อมูลในเครื่องก่อน · การบันทึกยังต้องออนไลน์จนมี Published Offline Authority</p>
        )}
      </section>

      {wasteOpen ? (
        <div className="pos-stock-action-modal" role="presentation">
          <section role="dialog" aria-modal="true" aria-labelledby="pos-stock-action-modal-title">
            <header>
              <div>
                <span>MORE Stock</span>
                <h3 id="pos-stock-action-modal-title">ของเสีย / ทิ้ง</h3>
              </div>
              <button type="button" onClick={closeWaste} aria-label="ปิด">×</button>
            </header>

            <div className="pos-stock-action-modal__body">
              <div className="pos-stock-action-modal__item">
                <strong>{item.canonical_name}</strong>
                <span>คงเหลือ {currentQuantity == null ? "—" : currentQuantity.toLocaleString("th-TH")}</span>
              </div>

              <label>
                <span>เหตุผล</span>
                <select value={reasonCode} onChange={(event) => { setReasonCode(event.target.value); setError(null); }} disabled={busy}>
                  <option value="">เลือกเหตุผล</option>
                  {wasteOptions.map((option) => <option key={option.reason_code} value={option.reason_code}>{option.label}</option>)}
                </select>
              </label>

              <label>
                <span>จำนวน</span>
                <input type="number" min="0" step="any" inputMode="decimal" value={quantity} onChange={(event) => { setQuantity(event.target.value); setError(null); }} placeholder="0" disabled={busy} />
              </label>

              <label>
                <span>รายละเอียด{selectedReason?.requires_note ? " *" : ""}</span>
                <textarea value={note} onChange={(event) => { setNote(event.target.value); setError(null); }} rows={3} maxLength={2000} placeholder="บอกสาเหตุที่เสีย ทิ้ง หมดอายุ หรือสูญหาย" disabled={busy} />
              </label>

              {!online ? <div className="pos-stock-action-modal__warning">ยังบันทึกไม่ได้ขณะออฟไลน์ เพราะ Owner ยังไม่ได้ Publish Offline Authority สำหรับ Action นี้</div> : null}
              {error ? <div className="pos-stock-action-modal__error">{error}</div> : null}
            </div>

            <footer>
              <button type="button" className="secondary" onClick={closeWaste} disabled={busy}>ยกเลิก</button>
              <button type="button" className="primary" onClick={() => void submitWaste()} disabled={busy || !online || !selectedReason}>
                {busy ? "กำลังบันทึก…" : "ยืนยันรายการ"}
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {transferWorkspace ? createPortal(
        <StockTransferWorkspace
          identity={identity}
          deviceSecret={deviceSecret}
          sessionToken={sessionToken}
          items={transferWorkspace.items}
          runtime={transferWorkspace.runtime}
          seedItem={item}
          online={online}
          initialMode="SEND"
          onClose={() => setTransferWorkspace(null)}
        />,
        document.body,
      ) : null}
    </>
  );
}
